import { SelectComposition } from '@codeleap/web'
import { textVariant } from './Text'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'

const createSelectVariant = createStyles<SelectComposition>

const placeholderSize = 120

export const SelectStyles = {
  default: createSelectVariant(theme => ({
    wrapper: {
      ...theme.presets.column,
    },
    innerWrapper: {
      minHeight: theme.size.large,
      maxHeight: theme.size.large,
      borderRadius: theme.radius.regular,
      ...theme.spacing.paddingHorizontal(2),
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 1 }),
      paddingVertical: theme.spacing.value(0),
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      ...theme.presets.justifySpaceBetween,
      backgroundColor: theme.baseColors.neutralSolid0,
      position: 'relative',
      cursor: 'pointer',

      '*': {
        fontFamily: theme.typography.defaults.fontFamily,
      },
    },
    'innerWrapper:searchable': {
      cursor: 'text',
    },
    'innerWrapper:focus': {
      ...theme.border({ color: theme.baseColors.primarySolid300, width: 1 }),
    },
    'innerWrapper:error': {
      ...theme.border({ color: theme.baseColors.redSolid400, width: 1 }),
    },
    'innerWrapper:disabled': {
      ...theme.border({ color: theme.baseColors.neutralSolid200, width: 1 }),
      cursor: 'not-allowed',
    },
    input: {
      ...textVariant('p1'),
      flex: 1,
      width: '100%',
      color: theme.baseColors.neutralSolid1000,
      borderWidth: 0,
      margin: theme.spacing.value(0),
      padding: theme.spacing.value(0),
      height: '100%',
    },
    'input:focus': {
      caretColor: theme.baseColors.primarySolid300,
      border: 'none',
    },
    'input:disabled': {
      color: theme.baseColors.neutralSolid500,
      backgroundColor: 'transparent',
      cursor: 'not-allowed',
    },
    'input:error': {
      color: theme.baseColors.redSolid400,
    },
    inputContainer: {
      flex: 1,
      width: '100%',
      border: 'none',
      height: '100%',
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      ...theme.presets.justifySpaceBetween,
      padding: theme.spacing.value(0),
    },
    'inputContainer:focus': {
      border: 'none',
    },
    placeholder: {
      color: theme.baseColors.neutralSolid700,
      position: 'absolute',
    },
    'placeholder:disabled': {
      color: theme.baseColors.neutralSolid500,
    },
    valueWrapper: {
      padding: theme.spacing.value(0),
      margin: theme.spacing.value(0),
      height: '100%',
      width: '100%',
      display: 'flex',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    valueMultiple: {
      width: 'auto',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    iconIcon: {
      height: theme.values.iconSize[2],
      width: theme.values.iconSize[2],
      color: theme.baseColors.neutralSolid700,
    },
    'iconIcon:focus': {
      color: theme.baseColors.primarySolid300,
    },
    'iconIcon:disabled': {
      color: theme.baseColors.neutralSolid500,
      cursor: 'not-allowed',
    },
    'iconIcon:error': {
      color: theme.baseColors.redSolid400,
    },
    iconTouchableWrapper: {
      ...theme.spacing.padding(0),
      height: theme.values.iconSize[2],
      width: theme.values.iconSize[2],
      backgroundColor: theme.baseColors.neutralSolid0,
    },
    'iconTouchableWrapper:disabled': {
      backgroundColor: theme.baseColors.neutralSolid0,
    },
    leftIconTouchableWrapper: {
      ...theme.spacing.marginRight(2),
    },
    rightIconTouchableWrapper: {
      ...theme.spacing.marginLeft(2),
    },
    errorMessage: {
      ...textVariant('p4'),
      color: theme.baseColors.redSolid400,
      ...theme.spacing.marginTop(1),
      ...theme.spacing.paddingLeft(2),
    },
    dropdownIcon: {
      cursor: 'pointer',
    },
    clearIcon: {
      cursor: 'pointer',
    },
    itemsWrapper: {
      ...theme.presets.column,
      ...theme.spacing.gap(1),
    },
    itemWrapper: {
      width: '100%',
      height: '100%',
      minHeight: theme.size.large,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      borderRadius: theme.radius.regular,
      ...theme.spacing.padding(2),
      backgroundColor: theme.baseColors.neutralSolid0,

      transition: 'all 0.2s',

      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid200,
      },
    },
    'itemWrapper:focused': {
      backgroundColor: theme.baseColors.neutralSolid200,

      '&:hover': {
        backgroundColor: theme.baseColors.neutralSolid200,
      },
    },
    'itemWrapper:selected': {
      backgroundColor: theme.baseColors.primarySolid300,

      '&:hover': {
        backgroundColor: theme.baseColors.primarySolid300,
      },
    },
    'itemWrapper:selectedFocused': {
      backgroundColor: theme.baseColors.primarySolid300,

      '&:hover': {
        backgroundColor: theme.baseColors.primarySolid300,
      },
    },
    itemText: {
      ...textVariant('p1'),
      color: theme.baseColors.neutralSolid1000,
      textAlign: 'left',
    },
    'itemText:selected': {
      ...textVariant('h5'),
    },
    itemRightIcon: {
      height: theme.values.iconSize[2],
      width: theme.values.iconSize[2],
      color: theme.baseColors.neutralSolid1000,
    },
    listWrapper: {
      borderRadius: theme.radius.regular,
      width: '100%',
      cursor: 'default',
    },
    list: {
      ...theme.spacing.paddingVertical(0),
      ...theme.spacing.padding(2),
      maxHeight: theme.size.large * 4,
      overflowY: 'auto',
    },
    listPlaceholder: {
      minHeight: theme.size.large,
      ...theme.presets.column,
      ...theme.presets.center,
      ...theme.spacing.gap(2),
    },
    listPlaceholderIcon: {
      width: placeholderSize,
      ...theme.presets.center,
    },
    listPlaceholderText: {
      ...textVariant('h5'),
      color: theme.baseColors.neutralSolid600,
      textAlign: 'center',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...theme.presets.fullWidth,
      ...theme.presets.center,
    },
    listPlaceholderNoItems: {
      minHeight: theme.size.large,
      ...theme.presets.column,
      ...theme.presets.center,
      ...theme.spacing.gap(2),
    },
    listPlaceholderNoItemsIcon: {
      width: placeholderSize,
    },
    listPlaceholderNoItemsText: {
      ...textVariant('h5'),
      color: theme.baseColors.neutralSolid600,
      textAlign: 'center',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...theme.presets.fullWidth,
      ...theme.presets.center,
    },
    loadingIndicator: {
      minHeight: theme.size.large,
      ...theme.presets.center,
    },
    label: {
      ...textVariant('p2'),
      color: theme.baseColors.neutralSolid700,
      ...theme.spacing.marginBottom(1),
    },
    description: {
      ...textVariant('p4'),
      color: theme.baseColors.neutralSolid700,
      ...theme.spacing.marginBottom(1),
    },
  })),
}

StyleRegistry.registerVariants('Select', SelectStyles)
